import { SESSION_TOUCH_INTERVAL_S } from "@rago/web-shared";

/**
 * Coarse relative time for "last seen" captions — just now / N min ago /
 * N h ago / N d ago. One home for the helper the settings pages and the
 * top-bar server picker share.
 */
export function since(iso: string, now: number = Date.now()): string {
  const s = Math.floor((now - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

/** "12s", "1m 52s", "1h 04m" — how long something has been running. */
export function elapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

function parse(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Compact relative stamp for a badge (the profile's watched pill, the
 * history list's right column): "now", "5m", "6h", "3d", "2w" while the
 * moment is recent, then a date — "Mar 2" inside the current year,
 * "Dec 30, 2025" beyond it. A stamp from the future (a server clock ahead
 * of ours) reads "now".
 */
export function compactSince(iso: string, now: number = Date.now(), locale?: string): string {
  const d = parse(iso);
  if (!d) return "";
  const ms = now - d.getTime();
  if (ms < MINUTE_MS) return "now";
  if (ms < HOUR_MS) return `${Math.floor(ms / MINUTE_MS)}m`;
  if (ms < DAY_MS) return `${Math.floor(ms / HOUR_MS)}h`;
  if (ms < WEEK_MS) return `${Math.floor(ms / DAY_MS)}d`;
  if (ms < 5 * WEEK_MS) return `${Math.floor(ms / WEEK_MS)}w`;
  if (d.getFullYear() === new Date(now).getFullYear()) {
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(d);
  }
  return shortDate(iso, locale);
}

/** "Sep 6, 2026, 3:42 PM" — the badge's title / tooltip. */
export function fullDateTime(iso: string, locale?: string): string {
  const d = parse(iso);
  return d ? new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(d) : "";
}

/** "Sep 2026" — the Joined chip, "watched since joining". */
export function monthYear(iso: string, locale?: string): string {
  const d = parse(iso);
  return d ? new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" }).format(d) : "";
}

/** "Sep 6, 2026" — the "On since …" hint. */
export function shortDate(iso: string, locale?: string): string {
  const d = parse(iso);
  return d
    ? new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(d)
    : "";
}

/**
 * "3:42 PM" — the history list's right column, whose day header carries
 * the date. timeZone is for tests; the page uses the viewer's.
 */
export function clockTime(iso: string, locale?: string, timeZone?: string): string {
  const d = parse(iso);
  return d
    ? new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone }).format(d)
    : "";
}

/**
 * Security › Sessions "Last active". The server touches a session's
 * last_used_at at most every SESSION_TOUCH_INTERVAL_S, so anything inside
 * that window (or ahead of our clock) is "Active now" and lit; beyond it
 * the coarse "12m ago" / "2h ago" / "3 days ago" ladder.
 */
export function lastActive(iso: string, now: number = Date.now()): { label: string; live: boolean } {
  const d = parse(iso);
  if (!d) return { label: "", live: false };
  const ms = now - d.getTime();
  if (ms <= SESSION_TOUCH_INTERVAL_S * 1000) return { label: "Active now", live: true };
  if (ms < HOUR_MS) return { label: `${Math.floor(ms / MINUTE_MS)}m ago`, live: false };
  if (ms < DAY_MS) return { label: `${Math.floor(ms / HOUR_MS)}h ago`, live: false };
  const days = Math.floor(ms / DAY_MS);
  return { label: days === 1 ? "1 day ago" : `${days} days ago`, live: false };
}
